import { fixed, inches, shopSpeak, signedInches, trim } from './format';
import { require, requireFinite, step, type Solved } from './types';

export interface Limits {
  readonly max: number;
  readonly min: number;
  /** Width of the legal band. */
  readonly total: number;
  readonly nominal: number;
}

/**
 * Limits from a nominal and its two tolerances.
 *
 * `upper` and `lower` are both given as positive magnitudes, matching how a
 * print reads them: 1.250 +.002/−.000 is upper 0.002, lower 0.000. Passing a
 * negative lower is the classic sign-error, so it is rejected rather than
 * silently producing a band that runs backwards.
 */
export function limits(nominal: number, upper: number, lower: number): Solved<Limits> {
  requireFinite(nominal, 'nominal');
  requireFinite(upper, 'upper tolerance');
  requireFinite(lower, 'lower tolerance');
  require(upper >= 0, 'Give the upper tolerance as a positive amount');
  require(lower >= 0, 'Give the lower tolerance as a positive amount — the sign is implied');

  const max = nominal + upper;
  const min = nominal - lower;

  return {
    value: { max, min, total: max - min, nominal },
    /*
     * Step kinds are load-bearing, not decoration: the drill shows only
     * 'formula', 'substitute' and 'note' before she answers. Anything that
     * states a computed number therefore has to be 'compute' or 'result',
     * however conversational it reads — a chatty 'note' carrying the total
     * tolerance would hand over the answer to the question that asks for it.
     */
    steps: [
      step('formula', 'max = nominal + upper · min = nominal − lower'),
      step(
        'substitute',
        `${trim(nominal)} + ${trim(upper, 4)} for the top, ${trim(nominal)} − ${trim(lower, 4)} for the bottom`,
      ),
      step('compute', `${trim(nominal)} + ${trim(upper, 4)} = ${inches(max)}`),
      step('compute', `${trim(nominal)} − ${trim(lower, 4)} = ${inches(min)}`),
      step('result', `Legal range is ${inches(min)} to ${inches(max)}`),
      step(
        'result',
        `Total tolerance is the whole band, ${inches(max - min)} — not one side of it.`,
      ),
    ],
  };
}

/** Symmetric ± callout. */
export function bilateralLimits(nominal: number, tolerance: number): Solved<Limits> {
  require(tolerance >= 0, 'Give the tolerance as a positive amount');
  const solved = limits(nominal, tolerance, tolerance);
  return {
    value: solved.value,
    steps: [
      step('formula', '± means the same amount each way'),
      ...solved.steps.slice(1, 5),
      step(
        'result',
        `Total tolerance is ${inches(tolerance * 2)}, double the number on the print. This is the most common place to lose a mark.`,
      ),
    ],
  };
}

export type Verdict = 'good' | 'oversize' | 'undersize';

export interface Inspection {
  readonly verdict: Verdict;
  /** How far outside the band, 0 when good. */
  readonly outBy: number;
  /** Signed deviation from nominal. */
  readonly fromNominal: number;
  readonly limits: Limits;
}

/** Judge a measured part against its limits. */
export function inspect(measured: number, band: Limits): Solved<Inspection> {
  requireFinite(measured, 'measured size');

  const verdict: Verdict =
    measured > band.max ? 'oversize' : measured < band.min ? 'undersize' : 'good';
  const outBy =
    verdict === 'oversize' ? measured - band.max : verdict === 'undersize' ? band.min - measured : 0;
  const fromNominal = measured - band.nominal;

  const steps = [
    step('formula', `Limits are ${inches(band.min)} to ${inches(band.max)}`),
    step('substitute', `Part measures ${inches(measured)}`),
  ];

  if (verdict === 'good') {
    steps.push(
      step('result', 'Inside the band. Good part.'),
      step(
        'note',
        `It sits ${signedInches(fromNominal)} from nominal, with ${inches(band.max - measured)} to the top limit.`,
      ),
    );
  } else {
    steps.push(
      step(
        'compute',
        verdict === 'oversize'
          ? `${trim(measured, 4)} − ${trim(band.max, 4)} = ${fixed(outBy, 4)}`
          : `${trim(band.min, 4)} − ${trim(measured, 4)} = ${fixed(outBy, 4)}`,
      ),
      step('result', `${verdict === 'oversize' ? 'Oversize' : 'Undersize'} by ${inches(outBy)} — ${shopSpeak(outBy)}.`),
      step(
        'note',
        verdict === 'oversize'
          ? 'Still salvageable. There is material left to take.'
          : 'Below the low limit. You cannot put it back — this one is scrap.',
      ),
    );
  }

  return { value: { verdict, outBy, fromNominal, limits: band }, steps };
}

export type FitType = 'clearance' | 'interference' | 'line-to-line';

export interface Fit {
  readonly type: FitType;
  /** Positive is clearance, negative is interference. */
  readonly allowance: number;
}

export function fit(holeSize: number, shaftSize: number): Solved<Fit> {
  requireFinite(holeSize, 'hole size');
  requireFinite(shaftSize, 'shaft size');

  const allowance = holeSize - shaftSize;
  const type: FitType =
    Math.abs(allowance) < 1e-9 ? 'line-to-line' : allowance > 0 ? 'clearance' : 'interference';

  const describe: Record<FitType, string> = {
    clearance: `${inches(allowance)} of clearance — the shaft drops in with room to spare.`,
    interference: `${inches(Math.abs(allowance))} of interference — a press fit. It goes together with force or heat, not by hand.`,
    'line-to-line': 'Line-to-line. Same size, no room and no squeeze.',
  };

  return {
    value: { type, allowance },
    steps: [
      step('formula', 'allowance = hole − shaft'),
      step('substitute', `${trim(holeSize, 4)} − ${trim(shaftSize, 4)}`),
      step('result', describe[type]),
      step(
        'note',
        'Hole minus shaft, always in that order. A negative answer means the shaft is bigger, which is interference.',
      ),
    ],
  };
}

/**
 * How much to take off a diameter to hit a target.
 *
 * The trap this exists to teach: on a diameter, the cross-slide comes in half
 * the amount you want to remove, because the cut happens on both sides.
 */
export function stockToRemove(measured: number, target: number): Solved<{ onDiameter: number; perSide: number }> {
  requireFinite(measured, 'measured size');
  requireFinite(target, 'target size');
  require(measured >= target, 'You cannot add material — measured must be at or above target');

  const onDiameter = measured - target;
  const perSide = onDiameter / 2;

  return {
    value: { onDiameter, perSide },
    steps: [
      step('formula', 'Subtract where you want to be from where you are.'),
      step('substitute', `${trim(measured, 4)} − ${trim(target, 4)}`),
      step('result', `= ${inches(onDiameter)} on the diameter — ${shopSpeak(onDiameter)}`),
      step(
        'note',
        `On a diameter the tool only comes in half that: ${inches(perSide)} per side.`,
      ),
    ],
  };
}
